import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
    Home, Library, LayoutDashboard, LogOut, Users, User, MessageSquare, FileText,
    Flag, GraduationCap, CheckCircle2, XCircle, AlertTriangle,
    Info, School, Bell, BookOpen, Bookmark, Heart, Ticket, Megaphone, BarChart3, Inbox, SlidersHorizontal, Brain,
    RefreshCw, Wallet, Menu, ChevronDown
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useSiteFeatures } from "@/hooks/useSiteFeatures";
import { Button } from "@/components/ui/button";
import { SmartBackButton } from "@/components/ui/smart-back-button";
import { cn } from "@/lib/utils";
import { BrandLockup } from "@/components/shared/BrandLockup";
import { useNotificationStore, dismiss } from "@/lib/notification-store";
import { useNotifications } from "@/hooks/useNotifications";
import { useToofiPreferences } from "@/hooks/useToofiPreferences";
import { captureToofiAnalytics } from "@/lib/toofi-analytics";
import { useWebPushNotifications } from "@/hooks/useWebPushNotifications";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
    DropdownMenuGroup
} from "@/components/ui/dropdown-menu";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer";
import { useMediaQuery } from "@/hooks/use-media-query";
import { UserAvatar } from "@/components/ui/user-avatar";
import { toast } from "@/components/ui/sonner";
import { useProgressStore } from "@/lib/progress-store";
import { useOfflineSyncStatus } from "@/hooks/useOfflineSyncStatus";
import { HeaderNotificationCenter } from "@/components/layout/header/header-notification-center";
import { HeaderNotificationIsland } from "@/components/layout/header/header-notification-island";
import { ToofiNudgeDetailDrawer } from "@/components/layout/header/toofi-nudge-detail-drawer";
import { HeaderOfflineSyncPill } from "@/components/layout/header/header-offline-sync-pill";
import { capturePublicAnalytics } from "@/lib/product-analytics";
import {
    buildPublicLoginPath,
    buildPublicSignupPath,
    PUBLIC_MARKETING_PATHS,
    PUBLIC_MARKETING_PRIMARY_NAV,
    PUBLIC_MARKETING_SECONDARY_NAV,
} from "@/lib/public-marketing";

// --- HELPER FUNCTIONS ---
const getPageTitle = (pathname: string): string => {
    const routes: Record<string, string> = {
        "/": "Home",
        "/features": "Features",
        "/toofi": "Toofi",
        "/offline-study": "Offline Study",
        "/trusted-resources": "Trusted Resources",
        "/for-publishers": "For Publishers",
        "/pricing": "Pricing",
        "/dashboard": "Dashboard",
        "/library": "Library",
        "/my-library": "My Library",
        "/profile": "Profile",
        "/student/inbox": "Inbox",
        "/student/study-time": "Study Time",
        "/student/orders": "Orders & Access",
        "/auth": "Sign In",
        "/contact": "Contact",
        "/about": "About",
        "/install": "Install Guide",
        "/help/how-to-pay": "How To Pay",
        "/privacy": "Privacy",
        "/terms": "Terms",
        "/refund-policy": "Refund Policy",
        "/publisher-agreement": "Publisher Agreement",
        "/copyright": "Copyright",
        "/share/study-plan": "Shared Study Plan",
        "/admin": "Admin Dashboard",
        "/admin/inbox": "Admin Inbox",
        "/admin/analytics": "Student Analytics",
        "/admin/feature-flags": "Feature Flags",
        "/admin/resources": "Manage Resources",
        "/admin/users": "User Management",
        "/admin/plans": "AI Plans",
        "/admin/bundle-sales": "Bundle Sales",
        "/admin/ai-plan-orders": "AI Plan Orders",
        "/admin/logs": "System Logs",
        "/admin/announcements": "Announcements",
        "/admin/statistics": "Admin Statistics",
        "/publisher/requests": "Access Requests",
        "/publisher/library": "Assets Library",
        "/publisher/submissions": "Submissions",
        "/publisher/waiting": "Application Status",
        "/publisher/profile": "Settings",
        "/publisher/statistics": "Publisher Statistics",
    };
    if (pathname.startsWith("/student/passes/")) return "Pass Details";
    if (pathname.startsWith("/resource/")) return "Resource Detail";
    return routes[pathname] || "Athr";
};

const getIslandShellTone = (type: string, scrolled: boolean) => {
    const base = scrolled
        ? "bg-white border-[rgba(226,232,240,0.84)] shadow-[0_18px_34px_-28px_rgba(15,23,42,0.12)]"
        : "bg-white border-[rgba(226,232,240,0.78)] shadow-[0_16px_30px_-28px_rgba(15,23,42,0.1)]";

    switch (type) {
        case "success":
            return cn(base, "border-[rgba(167,243,208,0.96)] bg-[rgba(236,253,245,1)] shadow-[0_20px_36px_-28px_rgba(5,150,105,0.14)]");
        case "error":
            return cn(base, "border-[rgba(254,202,202,0.96)] bg-[rgba(254,242,242,1)] shadow-[0_20px_36px_-28px_rgba(220,38,38,0.12)]");
        case "warning":
            return cn(base, "border-[rgba(253,230,138,0.96)] bg-[rgba(255,251,235,1)] shadow-[0_20px_36px_-28px_rgba(217,119,6,0.12)]");
        case "info":
            return cn(base, "border-[rgba(191,219,254,0.96)] bg-[rgba(239,246,255,1)] shadow-[0_20px_36px_-28px_rgba(37,99,235,0.12)]");
        case "library":
            return cn(base, "border-[rgba(191,219,254,0.96)] bg-[rgba(239,246,255,1)] shadow-[0_20px_36px_-28px_rgba(37,99,235,0.12)]");
        case "sparkles":
            return cn(base, "border-[rgba(219,234,254,0.96)] bg-[rgba(245,247,251,1)] shadow-[0_18px_34px_-28px_rgba(15,23,42,0.1)]");
        default:
            return cn(base);
    }
};

export const DynamicHeader = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, signOut, profile, library, isAdmin, isLibraryOwner } = useAuth();
    const { toofiEnabled, toofiPlansEnabled } = useSiteFeatures();
    const [scrolled, setScrolled] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const notification = useNotificationStore();
    const {
        notifications,
        notificationItems,
        inboxItems,
        unreadCount,
        inboxUnreadCount,
        markAsRead,
        markAllAsRead,
        getNotificationById,
        snoozeToofiNudge,
        markInboxMessageAsRead,
        markAllInboxAsRead,
    } = useNotifications();
    const isStudentInboxEnabled = useFeatureFlag("admin_inbox") && !isAdmin;
    const {
        isSupported: isPushSupported,
        permission: pushPermission,
        isEnabled: isPushEnabled,
        isLoading: isPushLoading,
        hasConfig: hasPushConfig,
        enable: enablePush,
        disable: disablePush,
    } = useWebPushNotifications();
    const isDesktop = useMediaQuery("(min-width: 768px)");
    const {
        isActive: isProgressActive,
        progress,
        label: progressLabel,
        theme: progressTheme,
    } = useProgressStore();
    const offlineSync = useOfflineSyncStatus();
    const [dismissedOfflineSyncSignature, setDismissedOfflineSyncSignature] = useState<string | null>(null);

    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isPublicMenuOpen, setIsPublicMenuOpen] = useState(false);
    const [notificationTab, setNotificationTab] = useState<"notifications" | "inbox">("notifications");
    const [selectedToofiNotificationId, setSelectedToofiNotificationId] = useState<string | null>(null);
    const [isImmersiveSurface, setIsImmersiveSurface] = useState(false);

    useEffect(() => {
        const checkImmersive = () => {
            const hasDialog = document.body.style.overflow === 'hidden' || 
                             document.body.hasAttribute('data-scroll-locked') ||
                             document.querySelector('[role="dialog"]:not(.header-menu)');
            setIsImmersiveSurface(!!hasDialog);
        };
        
        const observer = new MutationObserver(checkImmersive);
        observer.observe(document.body, { attributes: true, childList: true, subtree: true });
        checkImmersive();
        
        return () => observer.disconnect();
    }, []);

    const isNotifying = notification.isVisible;
    const isNotificationVisible = isNotifying && !isProgressActive;
    const hasIslandAction = Boolean(notification.link || notification.action);
    
    // STRICT GATE: The "Island" shell only emerges for Progress or Actionable alerts.
    const islandMode = isProgressActive 
        ? "progress" 
        : (isNotificationVisible && (hasIslandAction || notification.type === "success" || notification.type === "sparkles" || notification.type === "info")) 
            ? "action" 
            : "nav";
            
    const isIslandActive = islandMode !== "nav";
    const canManagePush = user && isPushSupported && hasPushConfig;
    const pushButtonLabel = isPushEnabled ? "Disable phone push" : "Enable phone push";
    const totalUnreadCount = unreadCount + inboxUnreadCount;
    const activePanelTitle = notificationTab === "notifications" ? "Notifications" : "Inbox";
    const activePanelSubtitle = notificationTab === "notifications" ? "Latest account activity" : "Messages from admins and support";
    const isDashboardPathForTone =
        location.pathname === "/dashboard" ||
        location.pathname.startsWith("/dashboard/") ||
        location.pathname === "/student-dashboard" ||
        location.pathname.startsWith("/student-dashboard/");
    const headerShellTone = isDashboardPathForTone
        ? (
            scrolled
                ? "bg-[var(--athr-canvas)] border-[rgba(195,198,215,0.34)] shadow-[0_12px_24px_-20px_rgba(15,23,42,0.08)]"
                : "bg-[var(--athr-canvas)] border-[rgba(195,198,215,0.3)] shadow-[0_10px_20px_-20px_rgba(15,23,42,0.06)]"
        )
        : getIslandShellTone(notification.type, scrolled);
    const { preferences: toofiPreferences, savePreferences: saveToofiPreferences } = useToofiPreferences(user?.id);
    const selectedToofiNotification = selectedToofiNotificationId ? getNotificationById(selectedToofiNotificationId) : null;

    useEffect(() => {
        if (!offlineSync.visible) {
            setDismissedOfflineSyncSignature(null);
            return;
        }

        setDismissedOfflineSyncSignature((current) =>
            current && current !== offlineSync.signature ? null : current,
        );
    }, [offlineSync.signature, offlineSync.visible]);

    const showOfflineSync = offlineSync.visible && dismissedOfflineSyncSignature !== offlineSync.signature;

    const handleActiveNotificationClick = () => {
        if (notification.link) {
            navigate(notification.link);
            dismiss();
            return;
        }

        if (notification.action) {
            notification.action();
            dismiss();
        }
    };

    const handleNotificationTabChange = (tab: "notifications" | "inbox") => {
        setNotificationTab(tab);
    };

    const handleMarkAllNotificationsRead = () => {
        void markAllAsRead();
    };

    const handleMarkAllInboxRead = () => {
        void markAllInboxAsRead();
    };

    const handleNotificationSelect = (id: string, link: string | null) => {
        void markAsRead(id);
        const selectedNotification = notifications.find((item) => item.id === id);
        if (selectedNotification?.type === "toofi_nudge") {
            captureToofiAnalytics("toofi_nudge_why_opened", {
                notification_id: id,
                nudge_event_type: selectedNotification.nudgeEventType ?? null,
            });
            setSelectedToofiNotificationId(id);
            return;
        }
        if (link) {
            setIsNotifOpen(false);
            navigate(link);
        }
    };

    const handleInboxSelect = (id: string, link: string | null) => {
        void markInboxMessageAsRead(id);
        if (link) {
            setIsNotifOpen(false);
            navigate(link);
        }
    };

    const handlePushToggle = async () => {
        if (!canManagePush) return;

        const ok = isPushEnabled ? await disablePush() : await enablePush();

        if (ok) {
            toast.success(isPushEnabled ? "Push disabled" : "Push enabled", {
                description: isPushEnabled
                    ? "Phone notification-center alerts were turned off."
                    : "You will now receive phone notification-center alerts.",
            });
            return;
        }

        if (pushPermission === "denied") {
            toast.error("Browser blocked notifications", {
                description: "Allow notifications in browser settings and try again.",
            });
            return;
        }

        toast.error("Could not update push settings", {
            description: "Please try again in a few seconds.",
        });
    };

    const handleOpenSelectedToofiNotification = () => {
        if (!selectedToofiNotification) return;
        setSelectedToofiNotificationId(null);
        setIsNotifOpen(false);
        if (selectedToofiNotification.link) {
            navigate(selectedToofiNotification.link);
        }
    };

    const handleSnoozeSelectedToofiNotification = async (hours: number) => {
        if (!selectedToofiNotification) return;
        const untilIso = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
        await snoozeToofiNudge(selectedToofiNotification.id, untilIso);
        captureToofiAnalytics("toofi_nudge_snoozed", {
            notification_id: selectedToofiNotification.id,
            nudge_event_type: selectedToofiNotification.nudgeEventType ?? null,
            snooze_hours: hours,
        });
        setSelectedToofiNotificationId(null);
        toast.success("Toofi reminder snoozed.");
    };

    const handleSnoozeSelectedToofiNotificationUntilTomorrow = async () => {
        if (!selectedToofiNotification) return;
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(8, 0, 0, 0);
        await snoozeToofiNudge(selectedToofiNotification.id, tomorrow.toISOString());
        captureToofiAnalytics("toofi_nudge_snoozed", {
            notification_id: selectedToofiNotification.id,
            nudge_event_type: selectedToofiNotification.nudgeEventType ?? null,
            snooze_hours: "until_tomorrow",
        });
        setSelectedToofiNotificationId(null);
        toast.success("Toofi reminder snoozed until tomorrow.");
    };

    const handlePauseToofiNudgesForDay = async () => {
        const pausedUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        await saveToofiPreferences({
            ...toofiPreferences,
            pausedUntil,
        });
        captureToofiAnalytics("toofi_nudges_paused", {
            paused_until: pausedUntil,
            source: "dynamic_header",
        });
        setSelectedToofiNotificationId(null);
        toast.success("Toofi nudges paused for 24 hours.");
    };

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        if (!isStudentInboxEnabled && notificationTab === "inbox") {
            setNotificationTab("notifications");
        }
    }, [isStudentInboxEnabled, notificationTab]);

    useEffect(() => {
        const handleFocus = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            if (['INPUT', 'TEXTAREA'].includes(target.tagName)) {
                setIsKeyboardOpen(true);
            }
        };
        const handleBlur = () => setIsKeyboardOpen(false);

        window.addEventListener('focusin', handleFocus);
        window.addEventListener('focusout', handleBlur);
        return () => {
            window.removeEventListener('focusin', handleFocus);
            window.removeEventListener('focusout', handleBlur);
        };
    }, []);

    const isAuthPage = location.pathname.startsWith("/auth") || location.pathname === "/onboarding";
    const isLoggedOutPublicMarketing = !user && PUBLIC_MARKETING_PATHS.has(location.pathname);
    const isDashboardSurface =
        location.pathname === "/dashboard" ||
        location.pathname.startsWith("/dashboard/") ||
        location.pathname === "/student-dashboard" ||
        location.pathname.startsWith("/student-dashboard/");
    const pageTitle = isDashboardSurface ? "" : getPageTitle(location.pathname);
    const publicSurfaceParam = location.pathname === "/" ? "home" : location.pathname.replace(/^\//, "");
    const primaryPublicNav = PUBLIC_MARKETING_PRIMARY_NAV.filter((item) => {
        if (item.href === "/toofi") return toofiEnabled;
        if (item.href === "/pricing") return toofiPlansEnabled;
        return true;
    });
    const secondaryPublicNav = PUBLIC_MARKETING_SECONDARY_NAV;
    const dashboardShellTone = scrolled
        ? "supports-[backdrop-filter]:bg-[var(--athr-canvas)] bg-[var(--athr-canvas)] backdrop-blur-0 border-[rgba(195,198,215,0.34)] shadow-[0_12px_24px_-20px_rgba(15,23,42,0.08)]"
        : "supports-[backdrop-filter]:bg-[var(--athr-canvas)] bg-[var(--athr-canvas)] backdrop-blur-0 border-[rgba(195,198,215,0.3)] shadow-[0_10px_20px_-20px_rgba(15,23,42,0.06)]";
    const captureSecondaryNavClick = (label: string, targetPath: string) => {
        capturePublicAnalytics("public_student_secondary_nav_clicked", {
            surface: publicSurfaceParam,
            cta_label: label,
            cta_position: "header-secondary",
            target_path: targetPath,
            authenticated: false,
        });
    };

    return (
        <>
        <header className={cn(
            "dynamic-header",
            "fixed top-0 left-0 right-0 flex flex-col items-center justify-center gap-2 pointer-events-none transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]",
            isIslandActive ? "p-2 pt-[calc(0.5rem+env(safe-area-inset-top))] z-[505]" : "p-4 pt-[calc(1rem+env(safe-area-inset-top))] z-[495]",
            (isKeyboardOpen || (isImmersiveSurface && !isIslandActive && !isNotifOpen && !isProfileOpen) || (!isDesktop && user && !isIslandActive)) ? "-translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100"
        )}>
            <div className="relative flex min-h-[4.5rem] w-full items-start justify-center">
                <motion.div
                    layout
                    initial={false}
                    transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 30,
                        mass: 1
                    }}
                    className={cn(
                        "relative pointer-events-auto overflow-hidden border backdrop-blur-2xl transition-all duration-500",
                        isIslandActive
                            ? headerShellTone
                            : isDashboardSurface
                                ? dashboardShellTone
                                : scrolled
                                    ? "supports-[backdrop-filter]:bg-white/80 bg-white/86 border-slate-200/60 shadow-[0_20px_40px_-24px_rgba(15,23,42,0.12)]"
                                    : "supports-[backdrop-filter]:bg-white/70 bg-white/80 border-slate-200/40 shadow-[0_18px_34px_-26px_rgba(15,23,42,0.1)]",
                        isIslandActive
                            ? cn(
                                islandMode === "progress"
                                    ? "h-[3.75rem] w-[90vw] md:w-[420px] rounded-[2rem] px-2 py-0 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)]"
                                    : "min-h-[4rem] w-[92vw] md:w-fit md:min-w-[380px] md:max-w-[520px] rounded-[2.5rem] px-6 py-3 shadow-[0_25px_60px_-12px_rgba(0,0,0,0.12)]",
                              )
                            : isLoggedOutPublicMarketing
                                ? "flex h-14 w-[96%] max-w-6xl items-center justify-between rounded-full px-3 sm:px-4"
                                : isDashboardSurface
                                    ? "flex h-14 w-[94%] max-w-[1100px] items-center justify-between rounded-full px-3 sm:px-4"
                                    : "flex h-14 w-[95%] max-w-5xl items-center justify-between rounded-full px-4",
                    )}
                >
                    <AnimatePresence mode="wait">
                        {isIslandActive ? (
                            <motion.div
                                key="island-content"
                                initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
                                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                                exit={{ opacity: 0, scale: 0.95, filter: "blur(5px)" }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                className="relative z-20 h-full w-full flex items-center justify-center"
                            >
                                <HeaderNotificationIsland
                                    islandMode={islandMode}
                                    isIslandActive={isIslandActive}
                                    notification={{
                                        type: notification.type,
                                        message: notification.message,
                                        description: notification.description,
                                        actionLabel: notification.actionLabel,
                                    }}
                                    onAction={handleActiveNotificationClick}
                                    progress={progress}
                                    progressLabel={progressLabel}
                                    progressTheme={progressTheme}
                                />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="nav-content"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                className="h-full w-full"
                            >
                                <div className="flex h-full w-full items-center justify-between px-4">
                                    {isLoggedOutPublicMarketing ? (
                                        <>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <BrandLockup
                                                    to="/"
                                                    className="translate-y-[1px]"
                                                    titleClassName="text-[1.125rem]"
                                                    subtitleClassName="text-[9px]"
                                                />
                                            </div>
                                            <nav className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-1 lg:flex">
                                                {primaryPublicNav.map((item) => (
                                                    <Link
                                                        key={item.href}
                                                        to={item.href}
                                                        className={cn(
                                                            "public-nav-pill",
                                                            location.pathname === item.href && "public-nav-pill-active",
                                                        )}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                ))}
                                            </nav>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="hidden h-11 rounded-full px-4 text-sm font-medium text-[var(--athr-text-secondary)] hover:bg-[var(--athr-surface-soft)] lg:inline-flex"
                                                        >
                                                            More
                                                            <ChevronDown className="ms-1.5 h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" sideOffset={12} className="w-56 rounded-[1.5rem] border-[rgba(226,232,240,0.8)] bg-white/94 p-2 shadow-[0_22px_44px_-28px_rgba(15,23,42,0.16)] backdrop-blur-2xl">
                                                        <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.16em] text-[var(--athr-text-tertiary)]">
                                                            More
                                                        </DropdownMenuLabel>
                                                        {secondaryPublicNav.map((item) => (
                                                            <DropdownMenuItem key={item.href} asChild>
                                                                <Link to={item.href}>{item.label}</Link>
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                                <Link to="/auth/login" className="hidden sm:block">
                                                    <Button size="sm" variant="ghost" className="h-11 rounded-full px-5 text-base font-medium">Sign in</Button>
                                                </Link>
                                                <Link to="/auth/login?mode=signup">
                                                    <Button size="sm" className="h-11 rounded-full bg-[var(--athr-brand)] px-6 text-base font-bold text-white shadow-[0_12px_24px_-10px_rgba(var(--athr-brand-rgb),0.3)]">Start free</Button>
                                                </Link>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <SmartBackButton />
                                                <BrandLockup to={isLibraryOwner ? "/publisher" : "/"} className="translate-y-[1px]" titleClassName="text-[1.125rem]" subtitleClassName="text-[9px]" />
                                            </div>
                                            {pageTitle && (
                                                <div className="pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center justify-center md:flex">
                                                    <span className="font-display text-[13px] font-semibold tracking-[0.06em] text-slate-500">{pageTitle}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 shrink-0">
                                                {user && (
                                                    isDesktop ? (
                                                        <DropdownMenu open={isNotifOpen} onOpenChange={setIsNotifOpen}>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="relative h-11 w-11 rounded-full bg-[var(--athr-surface-soft)] p-0 transition-[transform,opacity,background-color] duration-150 hover:bg-white active:scale-95">
                                                                    <Bell className="h-5 w-5 text-[var(--athr-text-secondary)]" />
                                                                    {totalUnreadCount > 0 && <span className="absolute top-[8px] right-[8px] h-3 w-3 rounded-full bg-[var(--athr-danger)] ring-2 ring-white" />}
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end" sideOffset={12} className="header-menu w-[340px] rounded-[2rem] border-[rgba(226,232,240,0.82)] bg-white/95 p-2 shadow-xl backdrop-blur-3xl">
                                                                <HeaderNotificationCenter tab={notificationTab} onTabChange={handleNotificationTabChange} notifications={notificationItems} inboxItems={inboxItems} unreadCount={unreadCount} inboxUnreadCount={inboxUnreadCount} activePanelTitle={activePanelTitle} activePanelSubtitle={activePanelSubtitle} canManagePush={canManagePush} isPushLoading={isPushLoading} pushButtonLabel={pushButtonLabel} onPushToggle={handlePushToggle} onMarkAllNotificationsRead={handleMarkAllNotificationsRead} onMarkAllInboxRead={handleMarkAllInboxRead} onNotificationSelect={handleNotificationSelect} onInboxSelect={handleInboxSelect} showInboxTab={isStudentInboxEnabled} />
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    ) : (
                                                        <Drawer open={isNotifOpen} onOpenChange={setIsNotifOpen}>
                                                            <DrawerTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="relative h-11 w-11 rounded-full bg-[var(--athr-surface-soft)] p-0 transition-[transform,opacity,background-color] duration-150 hover:bg-white active:scale-95">
                                                                    <Bell className="h-5 w-5 text-[var(--athr-text-secondary)]" />
                                                                    {totalUnreadCount > 0 && <span className="absolute top-[8px] right-[8px] h-3 w-3 rounded-full bg-[var(--athr-danger)] ring-2 ring-white" />}
                                                                </Button>
                                                            </DrawerTrigger>
                                                            <DrawerContent className="max-h-[85vh] rounded-t-[2.5rem] bg-white/95 px-4 pb-8 pt-4 backdrop-blur-3xl">
                                                                <HeaderNotificationCenter tab={notificationTab} onTabChange={handleNotificationTabChange} notifications={notificationItems} inboxItems={inboxItems} unreadCount={unreadCount} inboxUnreadCount={inboxUnreadCount} activePanelTitle={activePanelTitle} activePanelSubtitle={activePanelSubtitle} canManagePush={canManagePush} isPushLoading={isPushLoading} pushButtonLabel={pushButtonLabel} onPushToggle={handlePushToggle} onMarkAllNotificationsRead={handleMarkAllNotificationsRead} onMarkAllInboxRead={handleMarkAllInboxRead} onNotificationSelect={handleNotificationSelect} onInboxSelect={handleInboxSelect} showInboxTab={isStudentInboxEnabled} compact />
                                                            </DrawerContent>
                                                        </Drawer>
                                                    )
                                                )}
                                                {user && (
                                                    isDesktop ? (
                                                        <DropdownMenu open={isProfileOpen} onOpenChange={setIsProfileOpen}>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="h-11 w-11 rounded-full p-0 transition-[transform,opacity,background-color] duration-150 hover:bg-[var(--athr-surface-soft)]">
                                                                    <UserAvatar src={profile?.avatar_url || undefined} name={profile?.full_name || user.email || "User"} className="h-10 w-10" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end" sideOffset={12} className="header-menu w-[280px] rounded-[2rem] border-[rgba(226,232,240,0.82)] bg-white/95 p-2 shadow-xl backdrop-blur-2xl">
                                                                <div 
                                                                    onClick={() => { setIsProfileOpen(false); navigate(isLibraryOwner ? "/publisher/profile" : "/profile"); }}
                                                                    className="mx-1 mb-2 rounded-2xl bg-slate-50 p-3 cursor-pointer hover:bg-slate-100 transition-colors group/island"
                                                                >
                                                                    <p className="truncate text-sm font-semibold group-hover/island:text-[var(--athr-brand)] transition-colors">{profile?.full_name || "User"}</p>
                                                                    <p className="truncate text-xs text-slate-500">{user.email}</p>
                                                                </div>
                                                                <DropdownMenuGroup>
                                                                    <DropdownMenuItem onClick={() => { setIsProfileOpen(false); navigate(isLibraryOwner ? "/publisher/profile" : "/profile"); }}>Profile</DropdownMenuItem>
                                                                    {!isAdmin && !isLibraryOwner && <DropdownMenuItem onClick={() => { setIsProfileOpen(false); navigate("/my-library"); }}>My Library</DropdownMenuItem>}
                                                                    <DropdownMenuItem onClick={async () => { setIsProfileOpen(false); await signOut(); navigate("/"); }} className="text-red-600">Sign Out</DropdownMenuItem>
                                                                </DropdownMenuGroup>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    ) : (
                                                        <Drawer open={isProfileOpen} onOpenChange={setIsProfileOpen}>
                                                            <DrawerTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="h-11 w-11 rounded-full p-0 transition-[transform,opacity,background-color] duration-150 hover:bg-[var(--athr-surface-soft)]">
                                                                    <UserAvatar src={profile?.avatar_url || undefined} name={profile?.full_name || user.email || "User"} className="h-10 w-10" />
                                                                </Button>
                                                            </DrawerTrigger>
                                                            <DrawerContent className="header-menu rounded-t-[2.5rem] bg-white/95 px-4 pb-12 pt-6 backdrop-blur-3xl">
                                                                <div 
                                                                    className="flex flex-col items-center space-y-3 mb-6 cursor-pointer"
                                                                    onClick={() => { setIsProfileOpen(false); navigate(isLibraryOwner ? "/publisher/profile" : "/profile"); }}
                                                                >
                                                                    <UserAvatar src={profile?.avatar_url || undefined} name={profile?.full_name || user.email || "User"} size="xl" />
                                                                    <h3 className="font-bold text-lg">{profile?.full_name || "User"}</h3>
                                                                </div>
                                                                <div className="space-y-3">
                                                                    <Button variant="outline" className="w-full justify-start rounded-full h-12" onClick={() => { setIsProfileOpen(false); navigate(isLibraryOwner ? "/publisher/profile" : "/profile"); }}>Profile</Button>
                                                                    <Button variant="destructive" className="w-full rounded-full h-12 mt-4" onClick={async () => { setIsProfileOpen(false); await signOut(); navigate("/"); }}>Sign Out</Button>
                                                                </div>
                                                            </DrawerContent>
                                                        </Drawer>
                                                    )
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
            {showOfflineSync && (
                <HeaderOfflineSyncPill
                    tone={offlineSync.tone}
                    label={offlineSync.label}
                    detail={offlineSync.detail}
                    onDismiss={() => setDismissedOfflineSyncSignature(offlineSync.signature)}
                />
            )}
        </header>
        <ToofiNudgeDetailDrawer
            open={Boolean(selectedToofiNotification)}
            onOpenChange={(open) => {
                if (!open) {
                    setSelectedToofiNotificationId(null);
                }
            }}
            notification={selectedToofiNotification}
            onOpenTarget={handleOpenSelectedToofiNotification}
            onSnoozeThreeHours={() => void handleSnoozeSelectedToofiNotification(3)}
            onSnoozeUntilTomorrow={() => void handleSnoozeSelectedToofiNotificationUntilTomorrow()}
            onPauseForDay={() => void handlePauseToofiNudgesForDay()}
        />
        </>
    );
};
